  // Decide how many items to show initially depending on screen width
  const computeInitialVisibleCount = (total = 0) => {
    if (typeof window === 'undefined') return 12;
    const w = window.innerWidth || 0;
    if (w >= 1280) return total || 1000; // xl and up: show all
    if (w >= 1024) return Math.min(36, total || 36); // lg: more items
    return Math.min(12, total || 12); // default mobile/tablet
  };

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../../supabase-client.js';
import CustomerHeader from '../../components/ui/CustomerHeader';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import FilterChips from './components/FilterChips';
import SortDropdown from './components/SortDropdown';
import BookGrid from './components/BookGrid';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LoadingSkeleton from './components/LoadingSkeleton';
import WhatsAppSelector from '../../components/ui/WhatsAppSelector';
import AdminService from '../../services/adminService';
import catalogImage from '../../assets/images/image.png';

const BookCatalogBrowseScreen = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeProductType, setActiveProductType] = useState(null);
  const [currentSort, setCurrentSort] = useState('relevance');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likedBooks, setLikedBooks] = useState([]);
  const [visibleCount, setVisibleCount] = useState(12);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const cartItemCount = 0;
  const favoriteCount = likedBooks.length;


  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = AdminService.isAuthenticated();
      const admin = AdminService.getCurrentAdmin();
      
      setIsAuthenticated(authenticated);
      setAdminData(admin);
    };

    checkAuth();
  }, []);

  // Función para cargar libros
  const fetchBooks = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch books from productbook table
      const { data: booksData, error: booksError } = await supabase
        .from('productbook')
        .select(`
          *,
          authorsbook(
            id_author,
            name_author
          )
        `)
        .order('id_product', { ascending: false });

      if (booksError) {
        console.error('Error loading books:', booksError);
      }

      // Fetch bibles from biblebook table
      const { data: biblesData, error: biblesError } = await supabase
        .from('biblebook')
        .select('*')
        .order('id_bible', { ascending: false });

      if (biblesError) {
        console.error('Error loading bibles:', biblesError);
      }

      // Fetch things from thingproduct table
      const { data: thingsData, error: thingsError } = await supabase
        .from('thingproduct')
        .select('*')
        .order('id_thing', { ascending: false });

      if (thingsError) {
        console.error('Error loading things:', thingsError);
      }

        // Process books data
        const processedBooks = (booksData || []).map(book => {
          const authorName = book.authorsbook?.name_author || 'Autor desconocido';
          
          return {
            ...book,
            authorName: authorName,
            author: authorName,
            product_type: 'book'
          };
        });

        // Process bibles data
        const processedBibles = (biblesData || []).map(bible => ({
          ...bible,
          product_type: 'bible',
          id_product: bible.id_bible, // For compatibility with existing grid logic
          bible_id: bible.id_bible,
          favorite_product: bible.favorite_product || false,
          favorite_count_product: bible.favorite_count_product || 0
        }));

        // Process things data
        const processedThings = (thingsData || []).map(thing => ({
          ...thing,
          product_type: 'thing',
          id_product: thing.id_thing, // For compatibility with existing grid logic
          thing_id: thing.id_thing,
          title_thing: thing.title_thing,
          price_thing: thing.price_thing,
          description_thing: thing.description_thing,
          type_thing: thing.type_thing, // Include the dynamic type_thing field
          available_thing: thing.available_thing,
          image_urls: thing.image_urls,
          favorite_product: thing.favorite_product || false,
          favorite_count_product: thing.favorite_count_product || 0
        }));

        // Combine all products
        const allProducts = [...processedBooks, ...processedBibles, ...processedThings];

        setBooks(allProducts);
        // Inicialmente mostramos más en pantallas grandes
        const initialCount = computeInitialVisibleCount(allProducts.length);
        setVisibleCount(initialCount);
        setFilteredBooks(allProducts.slice(0, initialCount));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Error al cargar productos');
        setLoading(false);
      }
    };

  // Cargar libros al montar el componente
  useEffect(() => {
    fetchBooks();
  }, []);

  // Si el usuario redimensiona a una pantalla XL, mostrar todos los ítems
  useEffect(() => {
    const onResize = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth >= 1280) {
        setVisibleCount((prev) => {
          if (books.length && prev < books.length) {
            return books.length;
          }
          return prev;
        });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [books.length]);

  // Exponer función de recarga para uso externo
  useEffect(() => {
    // Hacer la función disponible globalmente para que los formularios admin puedan usarla
    window.refreshBookCatalog = fetchBooks;
    
    // Cleanup al desmontar
    return () => {
      delete window.refreshBookCatalog;
    };
  }, []);

  // Buscar libros
  const handleSearch = (query) => {
    setSearchQuery(query);
    setVisibleCount(computeInitialVisibleCount(books.length));
  };

  // Función para manejar búsqueda desde el header
  const handleHeaderSearch = async (query) => {
    const trimmedQuery = query?.trim();
    
    if (!trimmedQuery) {
      setSearchResults([]);
      setShowSearchResults(false);
      handleSearch('');
      return;
    }

    setShowSearchResults(true);
    
    try {
      console.log('🔍 Iniciando búsqueda para:', trimmedQuery);
      
      // Buscar en tiempo real en todas las tablas
      const [booksResults, biblesResults, thingsResults] = await Promise.all([
        // Buscar en productbook - usando múltiples consultas para evitar error 400
        supabase
          .from('productbook')
          .select('id_product, title_product, author_product, price_product, image_product, category_product')
          .ilike('title_product', `%${trimmedQuery}%`)
          .limit(3)
          .then(async (titleResults) => {
            const authorResults = await supabase
              .from('productbook')
              .select('id_product, title_product, author_product, price_product, image_product, category_product')
              .ilike('author_product', `%${trimmedQuery}%`)
              .limit(2);
            
            // Combinar resultados y eliminar duplicados
            const combined = [...(titleResults.data || []), ...(authorResults.data || [])];
            const unique = combined.filter((item, index, self) => 
              index === self.findIndex(t => t.id_product === item.id_product)
            );
            
            return { data: unique.slice(0, 5), error: titleResults.error || authorResults.error };
          }),
        
        // Buscar en biblebook - usando múltiples consultas
        supabase
          .from('biblebook')
          .select('id_bible, title_bible, version_bible, price_bible, image_urls')
          .ilike('title_bible', `%${trimmedQuery}%`)
          .limit(2)
          .then(async (titleResults) => {
            const versionResults = await supabase
              .from('biblebook')
              .select('id_bible, title_bible, version_bible, price_bible, image_urls')
              .ilike('version_bible', `%${trimmedQuery}%`)
              .limit(2);
            
            // Combinar resultados y eliminar duplicados
            const combined = [...(titleResults.data || []), ...(versionResults.data || [])];
            const unique = combined.filter((item, index, self) => 
              index === self.findIndex(t => t.id_bible === item.id_bible)
            );
            
            return { data: unique.slice(0, 3), error: titleResults.error || versionResults.error };
          }),
        
        // Buscar en thingproduct
        supabase
          .from('thingproduct')
          .select('id_thing, title_thing, price_thing, image_urls')
          .ilike('title_thing', `%${trimmedQuery}%`)
          .limit(2)
      ]);

      console.log('📚 Resultados libros:', booksResults.data?.length || 0, booksResults.error);
      console.log('📖 Resultados biblias:', biblesResults.data?.length || 0, biblesResults.error);
      console.log('🎁 Resultados accesorios:', thingsResults.data?.length || 0, thingsResults.error);

      if (booksResults.error || biblesResults.error || thingsResults.error) {
        console.error('Error en búsqueda:', booksResults.error || biblesResults.error || thingsResults.error);
        return;
      }

      // Combinar y formatear resultados
      const allResults = [
        ...(booksResults.data || []).map(book => ({
          ...book,
          product_type: 'book',
          display_title: book.title_product,
          display_author: book.author_product,
          display_price: book.price_product,
          display_image: book.image_product
        })),
        ...(biblesResults.data || []).map(bible => ({
          ...bible,
          product_type: 'bible',
          id_product: bible.id_bible,
          display_title: bible.title_bible,
          display_author: bible.version_bible,
          display_price: bible.price_bible,
          display_image: bible.image_urls?.[0]
        })),
        ...(thingsResults.data || []).map(thing => ({
          ...thing,
          product_type: 'thing',
          id_product: thing.id_thing,
          display_title: thing.title_thing,
          display_author: 'Accesorio',
          display_price: thing.price_thing,
          display_image: thing.image_urls?.[0]
        }))
      ];

      if (error) {
        console.error('Error en búsqueda:', error);
        return;
      }

      console.log('🎯 Total resultados combinados:', allResults.length);
      console.log('📋 Resultados finales:', allResults);
      setSearchResults(allResults);
    } catch (err) {
      console.error('Error inesperado en búsqueda:', err);
    }

    // También actualizar la búsqueda principal
    handleSearch(query);
  };

  // Filtrar por categoría
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setVisibleCount(computeInitialVisibleCount(books.length));
  };

  // Filtrar por tipo de producto
  const handleProductTypeChange = (productType) => {
    setActiveProductType(productType);
    setVisibleCount(computeInitialVisibleCount(books.length));
  };


  // Ordenar
  const handleSortChange = (sortKey) => {
    setCurrentSort(sortKey);
  };

  // Like
  const handleLike = (bookId) => {
    setLikedBooks((prev) =>
      prev.includes(bookId) ? prev.filter((id) => id !== bookId) : [...prev, bookId]
    );
  };

  // Reintentar
  const handleRefresh = () => {
    window.location.reload();
  };

  // Categorías
  const getCategoriesFromDB = () => {
    const categories = [...new Set(books.map((b) => b.category_product))];
    return categories.filter(Boolean);
  };

  // Conteo por categoría
  const getBookCounts = () => {
    const counts = {};
    books.forEach((book) => {
      if (Array.isArray(book.categories_product)) {
        book.categories_product.forEach(categoryId => {
          counts[categoryId] = (counts[categoryId] || 0) + 1;
        });
      }
    });
    return counts;
  };

  //  Cargar más libros
  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 12, books.length));
  };

  // Recalcular lista filtrada cuando cambian filtros, búsqueda, o paginación
  useEffect(() => {
    let list = books;

    // Aplicar filtro de tipo de producto
    if (activeProductType) {
      if (activeProductType.startsWith('type_thing_')) {
        // Filter by specific type_thing value
        const typeThingValue = activeProductType.replace('type_thing_', '');
        list = list.filter((product) => {
          return product.product_type === 'thing' && product.type_thing === typeThingValue;
        });
      } else {
        // Filter by general product type
        list = list.filter((product) => {
          return product.product_type === activeProductType;
        });
      }
    }

    // Aplicar categoría (mantener para compatibilidad)
    if (activeCategory) {
      list = list.filter((book) => {
        if (!book.categories_product || !Array.isArray(book.categories_product)) {
          return false;
        }
        // Convertir activeCategory a número para comparación correcta
        const categoryId = parseInt(activeCategory);
        return book.categories_product.includes(categoryId);
      });
    }


    // Aplicar búsqueda con coincidencias parciales para todos los tipos de productos
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((product) => {
        if (product.product_type === 'book') {
          return product.title_product?.toLowerCase().includes(q) ||
                 (product.authorName && product.authorName.toLowerCase().includes(q));
        } else if (product.product_type === 'bible') {
          return product.title_bible?.toLowerCase().includes(q) ||
                 product.version_bible?.toLowerCase().includes(q);
        } else if (product.product_type === 'thing') {
          return product.title_thing?.toLowerCase().includes(q);
        }
        return false;
      });
    }

    // Aplicar orden
    let sorted = [...list];
    switch (currentSort) {
      case 'price-asc':
        sorted.sort((a, b) => {
          let priceA = 0;
          let priceB = 0;
          
          // Obtener el precio correcto según el tipo de producto
          if (a.product_type === 'book') {
            priceA = a.price_original_product || a.price_product || 0;
          } else if (a.product_type === 'bible') {
            priceA = a.price_bible || 0;
          } else if (a.product_type === 'thing') {
            priceA = a.price_thing || 0;
          }
          
          if (b.product_type === 'book') {
            priceB = b.price_original_product || b.price_product || 0;
          } else if (b.product_type === 'bible') {
            priceB = b.price_bible || 0;
          } else if (b.product_type === 'thing') {
            priceB = b.price_thing || 0;
          }
          
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        sorted.sort((a, b) => {
          let priceA = 0;
          let priceB = 0;
          
          // Obtener el precio correcto según el tipo de producto
          if (a.product_type === 'book') {
            priceA = a.price_original_product || a.price_product || 0;
          } else if (a.product_type === 'bible') {
            priceA = a.price_bible || 0;
          } else if (a.product_type === 'thing') {
            priceA = a.price_thing || 0;
          }
          
          if (b.product_type === 'book') {
            priceB = b.price_original_product || b.price_product || 0;
          } else if (b.product_type === 'bible') {
            priceB = b.price_bible || 0;
          } else if (b.product_type === 'thing') {
            priceB = b.price_thing || 0;
          }
          
          return priceB - priceA;
        });
        break;
      case 'rating':
        sorted.sort((a, b) => (b.rating_product || 0) - (a.rating_product || 0));
        break;
      case 'title':
        sorted.sort((a, b) => {
          let titleA = '';
          let titleB = '';
          
          // Obtener el título correcto según el tipo de producto
          if (a.product_type === 'book') {
            titleA = a.title_product || '';
          } else if (a.product_type === 'bible') {
            titleA = a.title_bible || '';
          } else if (a.product_type === 'thing') {
            titleA = a.title_thing || '';
          }
          
          if (b.product_type === 'book') {
            titleB = b.title_product || '';
          } else if (b.product_type === 'bible') {
            titleB = b.title_bible || '';
          } else if (b.product_type === 'thing') {
            titleB = b.title_thing || '';
          }
          
          return titleA.localeCompare(titleB);
        });
        break;
      case 'newest':
        sorted.sort((a, b) => (b.id_product || 0) - (a.id_product || 0));
        break;
      case 'popularity':
        sorted.sort((a, b) => (b.favorite_count_product || 0) - (a.favorite_count_product || 0));
        break;
      case 'relevance':
      default:
        // Mantener orden original por relevancia/ID descendente
        sorted.sort((a, b) => (b.id_product || 0) - (a.id_product || 0));
        break;
    }

    // Aplicar paginación visible
    setFilteredBooks(sorted.slice(0, visibleCount));
  }, [books, searchQuery, activeCategory, activeProductType, currentSort, visibleCount]);

  return (
    <div className="min-h-screen bg-background">
      <CustomerHeader 
        cartItemCount={cartItemCount} 
        isAuthenticated={isAuthenticated} 
        userName={adminData?.userName || ''} 
        onSearch={handleHeaderSearch}
      />
      <main className="pt-14 sm:pt-16 pb-16 md:pb-8">

        {/* Catalog Header Image - Full Width */}
        <div className="w-full mb-6">
          {/* Image Container */}
          <div className="relative w-full h-48 sm:h-64 lg:h-80 overflow-hidden shadow-lg">
            <img 
              src={catalogImage} 
              alt="Catálogo de Libros - Librería Lithay"
              className="w-full h-full object-cover"
            />
            {/* Desktop Text Overlay */}
            <div className="hidden sm:flex absolute inset-0 bg-gradient-to-r from-black/50 to-transparent">
              <div className="flex flex-col justify-center w-full px-6 sm:px-8 lg:px-12 py-6">
                <div className="mb-8">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                    Catálogo de Libros,<br /> Bíblias y más...
                  </h1>
                  <p className="text-sm sm:text-base lg:text-lg text-white/90">
                    Descubre nuestra colección de libros cristianos
                  </p>
                </div>
                
                {/* Social Media Icons - Horizontal */}
                <div className="flex space-x-3">
                  <a 
                    href="https://www.instagram.com/librerialithay/?hl=es" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-white/20 hover:bg-[#c37046] text-white hover:text-white transition-all duration-200 rounded-full p-2 backdrop-blur-sm"
                  >
                    <Icon name="Instagram" size={20} />
                  </a>
                  <a 
                    href="https://www.facebook.com/share/1RbbceVD1W/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-white/20 hover:bg-[#c37046] text-white hover:text-white transition-all duration-200 rounded-full p-2 backdrop-blur-sm"
                  >
                    <Icon name="Facebook" size={20} />
                  </a>
                  <WhatsAppSelector />
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile Text Below Image */}
          <div className="sm:hidden overflow-hidden shadow-lg" style={{backgroundColor: '#0b3452'}}>
            <div className="px-6 py-4">
              <h1 className="text-xl font-bold text-white mb-2">
                Catálogo de Libros,<br /> Bíblias y más...
              </h1>
              <p className="text-sm text-white/90 mb-4">
                Descubre nuestra colección de libros cristianos
              </p>
              
              {/* Social Media Icons - Mobile */}
              <div className="flex space-x-3">
                <a 
                  href="https://www.instagram.com/librerialithay/?hl=es" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-[#c37046] text-white hover:text-white transition-all duration-200 rounded-full p-2 backdrop-blur-sm flex items-center justify-center"
                >
                  <Icon name="Instagram" size={18} />
                </a>
                <a 
                  href="https://www.facebook.com/share/1RbbceVD1W/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-[#c37046] text-white hover:text-white transition-all duration-200 rounded-full p-2 backdrop-blur-sm flex items-center justify-center"
                >
                  <Icon name="Facebook" size={18} />
                </a>
                <WhatsAppSelector />
              </div>
            </div>
          </div>
        </div>

      <div className="container mx-auto px-2 py-1">
          {/* Search Results Display */}
          {showSearchResults && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="text-lg font-semibold text-card-foreground mb-3">
                Resultados de búsqueda
              </h3>
              {searchResults.length > 0 ? (
                <div className="space-y-3">
                  {searchResults.map((book) => (
                    <div
                      key={book.id_product}
                      className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                      onClick={() => {
                        navigate('/book-detail-screen', { 
                          state: { 
                            book: {
                              id: book.id_product,
                              title: book.title_product,
                              author: book.author_product,
                              price: book.price_product,
                              image: book.image_product,
                              category: book.category_product
                            }
                          }
                        });
                      }}
                    >
                      <Image
                        src={book.display_image || '/assets/images/book-placeholder.png'}
                        alt={book.display_title}
                        className="w-12 h-16 object-cover rounded flex-shrink-0"
                        low
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-card-foreground truncate">
                          {book.display_title}
                        </h4>
                        <p className="text-sm text-muted-foreground truncate">
                          {book.product_type === 'thing' ? book.display_author : `por ${book.display_author}`}
                        </p>
                        <p className="text-sm font-semibold text-primary">
                          ${book.display_price}
                        </p>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          book.product_type === 'book' ? 'bg-blue-100 text-blue-800' :
                          book.product_type === 'bible' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {book.product_type === 'book' ? 'Libro' :
                           book.product_type === 'bible' ? 'Biblia' : 'Accesorio'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No se encontraron resultados</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full">

            {/* FilterChips and SortDropdown container */}
            <div className="pb-3 mb-6 border-b border-border/50">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-3">
                {/* FilterChips */}
                <div className="flex-1 min-w-0">
                  <FilterChips
                    books={books}
                    activeProductType={activeProductType}
                    onProductTypeChange={handleProductTypeChange}
                  />
                </div>
                
                {/* SortDropdown */}
                <div className="flex justify-center md:justify-end md:pt-1">
                  <SortDropdown currentSort={currentSort} onSortChange={handleSortChange} />
                </div>
              </div>
            </div>


            {/* Mensaje de error */}
            {error && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <p className="text-destructive">{error}</p>
                  <Button variant="outline" onClick={handleRefresh} className="mt-2">
                    Reintentar
                  </Button>
                </div>
              </div>
            )}

            {/* Grid de libros */}
            <div className="book-grid-section">
              {loading ? (
                <LoadingSkeleton count={12} />
              ) : (
                <BookGrid books={filteredBooks} onLike={handleLike} likedBooks={likedBooks} />
              )}
            </div>

            {/* Botón para cargar más */}
            {!loading && filteredBooks.length >= 50 && (
              <div className="flex justify-center p-6 sm:p-8">
                <Button
                  variant="outline"
                  iconName="ChevronDown"
                  iconPosition="right"
                  className="text-sm sm:text-base"
                  onClick={handleLoadMore}
                >
                  Cargar Más Libros
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookCatalogBrowseScreen;

